import { useState, useEffect } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { ChevronLeft, ChevronRight, Sparkles, Link as LinkIcon, Loader2, Check, X, Plus, AlertCircle, Package, ImageIcon, Tags, ArrowUpRight } from 'lucide-react'
import { api } from '@/lib/api'
import { getProductScreenshotThumbnailUrl } from '@/lib/assets'
import { Button } from '@/components/ui/button'
import { ResultDetailModal } from '@/components/ResultDetailModal'
import { Input } from '@/components/ui/input'
import { toast } from '@/components/ui/toaster'
import type { ToolId, ForeplayAd, Product, ScoredAd } from '@/types'
import { cn } from '@/lib/utils'
import {
  ReferenceAdCard,
  CollapsibleAdSection,
  SelectionModeToggle,
  AdCountSelector,
  deduplicateAds,
  deduplicateBetweenLists,
  MAX_SELECTION,
} from '@/components/reference-ads'
import { AspectRatioSelector } from '@/components/variation/AspectRatioSelector'
import type { SelectionMode } from '@/components/reference-ads'
import { useCompetitorResearchStore } from '@/stores/competitorResearch'
import { useTasksStore } from '@/stores/tasks'

const STEPS_EXISTING = [
  { id: 1, name: 'Select Product' },
  { id: 2, name: 'Keywords' },
  { id: 3, name: 'Search Ads' },
  { id: 4, name: 'Select References' },
  { id: 5, name: 'Configure' },
  { id: 6, name: 'Generate' },
]

const STEPS_NEW = [
  { id: 1, name: 'Enter URL' },
  { id: 2, name: 'Keywords' },
  { id: 3, name: 'Search Ads' },
  { id: 4, name: 'Select References' },
  { id: 5, name: 'Configure' },
  { id: 6, name: 'Generate' },
]

export function CompetitorResearchPage() {
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const [searchParams] = useSearchParams()
  const preSelectedProduct = searchParams.get('product')

  // Store state
  const {
    entryMode,
    setEntryMode,
    currentStep,
    setCurrentStep,
    selectedProductId,
    setSelectedProductId,
    setSelectedProduct,
    productUrl,
    setProductUrl,
    keywords,
    setKeywords,
    competitorBrands,
    setCompetitorBrands,
    generatedDescription,
    setGeneratedDescription,
    searchResults,
    setSearchResults,
    selectedAdIds,
    setSelectedAdIds,
    scoredAdsMap,
    setScoredAdsMap,
    selectionMode,
    setSelectionMode,
    autoSelectCount,
    setAutoSelectCount,
    expandProductRelated,
    setExpandProductRelated,
    expandCompetitorBrands,
    setExpandCompetitorBrands,
    config,
    updateConfig,
    isSearching,
    setIsSearching,
    isScoring,
    setIsScoring,
  } = useCompetitorResearchStore()

  // Task store for generation tracking
  const { tasks, addTask, startPolling, removeTask, isPolling } = useTasksStore()

  // Find the latest competitor_research task
  const currentTask = Object.values(tasks)
    .filter(task => task.type === 'competitor_research')
    .sort((a, b) => b.createdAt - a.createdAt)[0]

  // Local state for immediate UI feedback (reset if no processing task on mount)
  const [isGenerating, setIsGenerating] = useState(false)

  // Reset isGenerating if we have a task that's not processing (e.g., after page refresh)
  useEffect(() => {
    if (currentTask && currentTask.status !== 'processing' && currentTask.status !== 'pending') {
      setIsGenerating(false)
    }
  }, [currentTask?.status])

  // Derived state from task - show processing immediately when generate button is clicked
  const taskStatus = currentTask?.status || (isGenerating ? 'processing' : null)
  const taskError = currentTask?.error || null
  const generatedResults = currentTask?.result || []

  // Local state for input fields
  const [newKeyword, setNewKeyword] = useState('')
  const [newBrand, setNewBrand] = useState('')

  // State for result detail modal
  const [selectedResultIdx, setSelectedResultIdx] = useState<number | null>(null)

  // Get reset function and staleness tracking from store
  const reset = useCompetitorResearchStore((state) => state.reset)
  const lastActiveAt = useCompetitorResearchStore((state) => state.lastActiveAt)

  const STALE_TIMEOUT_MS = 30 * 60 * 1000 // 30 minutes

  // Initialize from URL params on first render
  useEffect(() => {
    if (preSelectedProduct && currentStep === 1) {
      setSelectedProductId(preSelectedProduct)
      setCurrentStep(2)
    }
  }, [preSelectedProduct])

  // Reset if state is stale (user hasn't interacted for 30 minutes)
  useEffect(() => {
    if (lastActiveAt > 0 && Date.now() - lastActiveAt > STALE_TIMEOUT_MS) {
      reset()
    }
  }, [])

  // Resume polling if task still processing on mount
  useEffect(() => {
    if (currentTask && (currentTask.status === 'processing' || currentTask.status === 'pending')) {
      if (!isPolling(currentTask.id)) {
        startPolling(currentTask.id)
      }
    }
  }, [currentTask?.id, currentTask?.status, startPolling, isPolling])

  // Refresh subscription when task completes (to update credits display)
  useEffect(() => {
    if (currentTask?.status === 'completed' || currentTask?.status === 'failed') {
      queryClient.invalidateQueries({ queryKey: ['subscription'] })
    }
  }, [currentTask?.status, queryClient])

  // Handle stale searching/scoring state on mount (e.g., user left page during search)
  // If state says we're searching/scoring but no mutation is running, re-trigger
  useEffect(() => {
    if (currentStep === 3) {
      if (isSearching && !searchAdsMutation.isPending) {
        // Re-trigger search
        searchAdsMutation.mutate()
      } else if (isScoring && !scoreMutation.isPending && searchResults) {
        // Re-trigger scoring
        const productAds = deduplicateAds(searchResults.product_related)
        const competitorAds = deduplicateBetweenLists(productAds, deduplicateAds(searchResults.competitor_brands))
        const allAds = [...productAds, ...competitorAds]
        if (allAds.length > 0 && selectedProductId) {
          const allAdIds = allAds.map(ad => ad.id)
          scoreMutation.mutate({
            ad_ids: allAdIds,
            product_id: selectedProductId,
            top_n: allAdIds.length,
          })
        } else {
          setIsScoring(false)
        }
      }
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []) // Only run on mount


  const STEPS = entryMode === 'existing' ? STEPS_EXISTING : STEPS_NEW

  // Fetch products
  const { data: products } = useQuery({
    queryKey: ['products'],
    queryFn: () => api.getProducts(0, 100),
  })

  // Helper: Get deduplicated ads with scores merged from scoredAdsMap
  const getDeduplicatedAds = () => {
    if (!searchResults) return { productAds: [], competitorAds: [] }
    const productAds = deduplicateAds(searchResults.product_related).map(ad => {
      const scored = scoredAdsMap[ad.id]
      return scored || ad
    })
    const competitorAds = deduplicateBetweenLists(productAds, deduplicateAds(searchResults.competitor_brands)).map(ad => {
      const scored = scoredAdsMap[ad.id]
      return scored || ad
    })
    return { productAds, competitorAds }
  }

  // Helper: Select top N ads by score
  const selectTopN = (count: number) => {
    if (!searchResults) return
    const { productAds, competitorAds } = getDeduplicatedAds()
    const allAds = [...productAds, ...competitorAds]
    const sortedByScore = [...allAds].sort((a, b) => (b.overall_score || b.relevance_score || 0) - (a.overall_score || a.relevance_score || 0))
    const topNIds = sortedByScore.slice(0, count).map(ad => ad.id)
    setSelectedAdIds(topNIds)
  }

  // Helper: Handle selection mode change
  const handleModeChange = (mode: SelectionMode) => {
    setSelectionMode(mode)
    if (mode === 'auto') {
      selectTopN(autoSelectCount)
    }
  }

  // Helper: Handle auto-select count change
  const handleAutoSelectCountChange = (count: number) => {
    setAutoSelectCount(count)
    if (selectionMode === 'auto') {
      selectTopN(count)
    }
  }

  // Helper: Toggle ad selection (manual mode with limits)
  const toggleAdSelection = (adId: string) => {
    if (selectedAdIds.includes(adId)) {
      // Unselect - ensure at least 1 selected
      if (selectedAdIds.length > 1) {
        setSelectedAdIds(selectedAdIds.filter((id) => id !== adId))
      } else {
        toast({ title: 'At least 1 reference required', variant: 'destructive' })
      }
    } else {
      // Select - ensure max 10
      if (selectedAdIds.length < 10) {
        setSelectedAdIds([...selectedAdIds, adId])
      } else {
        toast({ title: 'Maximum 10 references allowed', variant: 'destructive' })
      }
    }
  }

  // Regenerate keywords for existing product
  const regenerateKeywordsMutation = useMutation({
    mutationFn: async (productId: string) => {
      return await api.regenerateKeywords(productId)
    },
    onSuccess: (data) => {
      setKeywords(data.keywords || [])
      setCompetitorBrands(data.competitor_brands || [])
    },
    onError: (error: any) => {
      // Skip if 401 - already handled by global interceptor
      if (error.response?.status === 401) return
      toast({
        title: 'Failed to regenerate keywords',
        description: 'Using existing keywords instead',
        variant: 'destructive',
      })
      // Fallback: keywords already set from product in handleProductSelect
    },
  })

  // Create product from URL (new mode)
  const createProductMutation = useMutation({
    mutationFn: async (url: string) => {
      return await api.createProductFromUrl(url)
    },
    onSuccess: (data) => {
      toast({ title: 'Product created', description: 'Keywords generated during URL parsing' })
      setSelectedProduct(data)
      setSelectedProductId(data.id)
      // Use keywords/competitor_brands from URL parsing (no need to call generateKeywords)
      setKeywords(data.keywords || [])
      setCompetitorBrands(data.competitor_brands || [])
      setGeneratedDescription(data.description || '')
      setCurrentStep(2)
    },
    onError: (error: any) => {
      // Skip if 401 - already handled by global interceptor
      if (error.response?.status === 401) return
      toast({
        title: 'Failed to parse URL',
        description: error.response?.data?.error?.message || 'Please check if the URL is valid',
        variant: 'destructive',
      })
    },
  })

  // Search reference ads v2
  const searchAdsMutation = useMutation({
    mutationFn: async () => {
      setIsSearching(true)
      return await api.searchReferenceAdsV2({
        description: generatedDescription,
        cate: '', // Category is optional, keywords and competitor_brands are primary search terms
        keywords,
        competitor_brands: competitorBrands,
        media_type: 'image',
        limit: 20,
      })
    },
    onSuccess: (data) => {
      setIsSearching(false)
      setSearchResults(data)
      // Deduplicate ads
      const productAds = deduplicateAds(data.product_related)
      const competitorAds = deduplicateBetweenLists(productAds, deduplicateAds(data.competitor_brands))
      const allAds = [...productAds, ...competitorAds]

      if (allAds.length > 0 && selectedProductId) {
        // Trigger parallel scoring for all ads
        const allAdIds = allAds.map(ad => ad.id)
        scoreMutation.mutate({
          ad_ids: allAdIds,
          product_id: selectedProductId,
          top_n: allAdIds.length, // Get scores for all ads
        })
      } else {
        // No ads found or no product selected
        setCurrentStep(4)
        toast({
          title: 'No reference ads found',
          description: 'Try adjusting your keywords or competitor brands',
          variant: 'destructive'
        })
      }
    },
    onError: () => {
      setIsSearching(false)
      toast({ title: 'Search failed', variant: 'destructive' })
    },
  })

  // Score reference ads (parallel scoring on backend)
  const scoreMutation = useMutation({
    mutationFn: async (params: { ad_ids: string[]; product_id: string; top_n?: number }) => {
      setIsScoring(true)
      return api.scoreReferenceAds(params)
    },
    onSuccess: (data) => {
      setIsScoring(false)
      // Update scored ads map with scores from response
      const newMap: Record<string, ForeplayAd> = {}
      data.scored_ads.forEach((scored: ScoredAd) => {
        newMap[scored.ad.id] = {
          ...scored.ad,
          overall_score: scored.overall_score,
        }
      })
      setScoredAdsMap(newMap)

      // Auto-select top N ads by score
      const topNIds = data.scored_ads
        .slice(0, autoSelectCount)
        .map((s: ScoredAd) => s.ad.id)
      setSelectedAdIds(topNIds)
      setCurrentStep(4)
    },
    onError: (error: any) => {
      setIsScoring(false)
      toast({
        title: 'Scoring failed',
        description: error.response?.data?.detail || 'Could not score ads. Please try again.',
        variant: 'destructive'
      })
      // Stay on step 3 so user can retry
    },
  })

  // Generate creatives
  const generateMutation = useMutation({
    mutationFn: () =>
      api.generateCompetitorResearch({
        product_id: selectedProductId,
        reference_ad_ids: selectedAdIds,
        keywords,
        model: config.model,
        count: selectedAdIds.length,
        custom_prompt: config.customPrompt || undefined,
        aspect_ratio: config.aspectRatio,
      }),
    onSuccess: (data) => {
      // Clear immediate generation state
      setIsGenerating(false)

      // Clear old completed/failed task before starting new one
      if (currentTask && (currentTask.status === 'completed' || currentTask.status === 'failed')) {
        removeTask(currentTask.id)
      }

      // Add to task store instead of navigating away
      addTask({
        id: data.id,
        type: 'competitor_research',
        status: 'processing',
        metadata: {
          productId: selectedProductId,
          count: selectedAdIds.length,
        },
      })
      startPolling(data.id)
      toast({ title: 'Generation started!' })
    },
    onError: () => {
      setIsGenerating(false)
      toast({ title: 'Generation failed', variant: 'destructive' })
    },
  })

  const handleProductSelect = (product: Product) => {
    setSelectedProductId(product.id)
    setSelectedProduct(product)
    // Set existing keywords as fallback, then regenerate via LLM for randomness
    setKeywords(product.keywords || [])
    setCompetitorBrands(product.competitor_brands || [])
    setGeneratedDescription(product.description || '')
    setCurrentStep(2)
    // Trigger keyword regeneration (results will update via onSuccess)
    regenerateKeywordsMutation.mutate(product.id)
  }

  const handleNext = () => {
    if (currentStep === 1) {
      if (entryMode === 'new' && productUrl) {
        createProductMutation.mutate(productUrl)
      }
    } else if (currentStep === 2) {
      setCurrentStep(3)
    } else if (currentStep === 3) {
      searchAdsMutation.mutate()
    } else if (currentStep === 4 || currentStep === 5) {
      setCurrentStep(currentStep + 1)
    } else if (currentStep === 6) {
      generateMutation.mutate()
    }
  }

  const canProceed = () => {
    switch (currentStep) {
      case 1:
        return entryMode === 'existing' ? !!selectedProductId : !!productUrl
      case 2:
        return keywords.length > 0 && !regenerateKeywordsMutation.isPending
      case 3:
        return true
      case 4:
        return selectedAdIds.length > 0
      case 5:
        return true
      case 6:
        return true
      default:
        return false
    }
  }

  const isLoading = createProductMutation.isPending

  return (
    <div className="max-w-4xl mx-auto space-y-6">
      {/* Loading Overlay */}
      {(isLoading || regenerateKeywordsMutation.isPending) && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-8 text-center max-w-sm mx-4">
            <Loader2 className="w-12 h-12 animate-spin mx-auto mb-4 text-primary" />
            <p className="font-medium text-lg">
              {isLoading ? 'Parsing product...' : 'Regenerating keywords...'}
            </p>
          </div>
        </div>
      )}

      {/* Entry Mode Toggle (only on step 1) */}
      {currentStep === 1 && !preSelectedProduct && (
        <div className="flex gap-2 p-1 bg-gray-100 rounded-lg w-fit">
          <button
            onClick={() => setEntryMode('existing')}
            className={cn(
              'px-4 py-2 rounded-md text-sm font-medium transition-colors',
              entryMode === 'existing' ? 'bg-white shadow text-primary' : 'text-gray-600 hover:text-gray-900'
            )}
          >
            Select Existing Product
          </button>
          <button
            onClick={() => setEntryMode('new')}
            className={cn(
              'px-4 py-2 rounded-md text-sm font-medium transition-colors',
              entryMode === 'new' ? 'bg-white shadow text-primary' : 'text-gray-600 hover:text-gray-900'
            )}
          >
            Create from URL
          </button>
        </div>
      )}

      {/* Steps */}
      <div className="flex items-start justify-around">
        {STEPS.map((step) => (
          <div key={step.id} className="flex flex-col items-center w-20">
            <div
              className={cn(
                'w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium',
                step.id < currentStep
                  ? 'bg-primary text-white'
                  : step.id === currentStep
                  ? 'bg-primary/20 text-primary border-2 border-primary'
                  : 'bg-gray-100 text-gray-400'
              )}
            >
              {step.id < currentStep ? <Check className="w-4 h-4" /> : step.id}
            </div>
            <span
              className={cn(
                'text-xs mt-1 text-center',
                step.id <= currentStep ? 'text-gray-700 font-medium' : 'text-gray-400'
              )}
            >
              {step.name}
            </span>
          </div>
        ))}
      </div>

      {/* Step Content */}
      <div className="bg-white rounded-xl border border-gray-200/60 shadow-sm p-6 min-h-[400px]">
        {/* Step 1: Select/Create Product */}
        {currentStep === 1 && (
          <div>
            {entryMode === 'existing' ? (
              <>
                <div className="mb-4">
                  <h2 className="text-lg font-semibold">Select a Product</h2>
                  <p className="mt-1 text-sm text-gray-500">
                    Choose a product profile to auto-generate keywords and find stronger references.
                  </p>
                </div>

                <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                  {products?.items.length ? (
                    products.items.map((product) => {
                      const isSelected = selectedProductId === product.id
                      const screenshotThumbnailUrl = getProductScreenshotThumbnailUrl(product)
                      return (
                        <button
                          key={product.id}
                          type="button"
                          onClick={() => handleProductSelect(product)}
                          className={cn(
                            'group relative overflow-hidden rounded-xl border bg-white text-left transition-all',
                            'hover:-translate-y-0.5 hover:shadow-md',
                            isSelected
                              ? 'border-primary bg-primary/[0.04] ring-2 ring-primary/15 shadow-md'
                              : 'border-gray-200 hover:border-primary/35'
                          )}
                        >
                          <div className="relative h-32 overflow-hidden border-b border-gray-100 bg-gradient-to-br from-slate-50 to-gray-100">
                            {screenshotThumbnailUrl ? (
                              <img
                                src={screenshotThumbnailUrl}
                                alt={product.product_name}
                                className="h-full w-full object-cover transition-transform duration-300 group-hover:scale-[1.02]"
                                loading="lazy"
                                decoding="async"
                              />
                            ) : (
                              <div className="flex h-full items-center justify-center text-gray-400">
                                <Package className="h-7 w-7" />
                              </div>
                            )}
                            {isSelected && (
                              <div className="absolute right-2 top-2 rounded-full bg-primary px-2 py-0.5 text-[11px] font-semibold text-white">
                                Selected
                              </div>
                            )}
                          </div>

                          <div className="space-y-3 p-4">
                            <div className="flex items-start justify-between gap-2">
                              <div className="min-w-0">
                                <h3 className="truncate text-sm font-semibold text-gray-900">
                                  {product.product_name}
                                </h3>
                                <p className="text-xs text-gray-500">
                                  {(product.language || 'en').toUpperCase()} market profile
                                </p>
                              </div>
                              <ArrowUpRight className="h-4 w-4 shrink-0 text-gray-400 transition-colors group-hover:text-primary" />
                            </div>

                            {product.description && (
                              <p className="line-clamp-2 text-xs text-gray-600">{product.description}</p>
                            )}

                            <div className="flex flex-wrap gap-2">
                              <span className="inline-flex items-center gap-1 rounded-full bg-gray-100 px-2 py-1 text-[11px] font-medium text-gray-600">
                                <ImageIcon className="h-3 w-3" />
                                {product.total_assets || 0} assets
                              </span>
                              <span className="inline-flex items-center gap-1 rounded-full bg-primary/10 px-2 py-1 text-[11px] font-medium text-primary">
                                <Tags className="h-3 w-3" />
                                {product.keywords?.length || 0} keywords
                              </span>
                            </div>
                          </div>
                        </button>
                      )
                    })
                  ) : (
                    <div className="col-span-full rounded-xl border border-dashed border-gray-300 bg-gray-50 p-8 text-center">
                      <Package className="mx-auto h-8 w-8 text-gray-400" />
                      <h3 className="mt-3 text-sm font-semibold text-gray-800">No products available</h3>
                      <p className="mt-1 text-sm text-gray-500">
                        Create your first product to start competitor research.
                      </p>
                      <Button variant="outline" className="mt-4" onClick={() => navigate('/products/new')}>
                        Create Product
                      </Button>
                    </div>
                  )}
                </div>
              </>
            ) : (
              <>
                <h2 className="text-lg font-semibold mb-4">Enter Product URL</h2>
                <div className="flex gap-3">
                  <div className="flex-1 relative">
                    <LinkIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                    <Input
                      type="url"
                      placeholder="https://your-store.com/products/..."
                      value={productUrl}
                      onChange={(e) => setProductUrl(e.target.value)}
                      className="pl-10"
                    />
                  </div>
                </div>
              </>
            )}
          </div>
        )}

        {/* Step 2: Keywords */}
        {currentStep === 2 && (
          <div className="space-y-6">
            <h2 className="text-lg font-semibold">Review Keywords</h2>

            {generatedDescription && (
              <div className="p-4 bg-gray-50 rounded-lg">
                <h3 className="text-sm font-medium text-gray-700 mb-2">Ad Description</h3>
                <p className="text-sm text-gray-600">{generatedDescription}</p>
              </div>
            )}

            <div>
              <h3 className="text-sm font-medium text-gray-700 mb-2">Search Keywords</h3>
              <div className="flex flex-wrap gap-2 mb-3">
                {keywords.map((kw, idx) => (
                  <span key={idx} className="inline-flex items-center gap-1 px-3 py-1.5 bg-primary/10 text-primary rounded-full text-sm">
                    {kw}
                    <button onClick={() => setKeywords(keywords.filter((_, i) => i !== idx))} className="hover:bg-primary/20 rounded-full p-0.5">
                      <X className="w-3 h-3" />
                    </button>
                  </span>
                ))}
              </div>
              <div className="flex gap-2">
                <Input
                  placeholder="Add keyword..."
                  value={newKeyword}
                  onChange={(e) => setNewKeyword(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' && newKeyword.trim()) {
                      setKeywords([...keywords, newKeyword.trim()])
                      setNewKeyword('')
                    }
                  }}
                />
                <Button variant="outline" size="icon" onClick={() => { if (newKeyword.trim()) { setKeywords([...keywords, newKeyword.trim()]); setNewKeyword('') } }}>
                  <Plus className="w-4 h-4" />
                </Button>
              </div>
            </div>

            <div>
              <h3 className="text-sm font-medium text-gray-700 mb-2">Competitor Brands</h3>
              <div className="flex flex-wrap gap-2 mb-3">
                {competitorBrands.map((brand, idx) => (
                  <span key={idx} className="inline-flex items-center gap-1 px-3 py-1.5 bg-orange-100 text-orange-700 rounded-full text-sm">
                    {brand}
                    <button onClick={() => setCompetitorBrands(competitorBrands.filter((_, i) => i !== idx))} className="hover:bg-orange-200 rounded-full p-0.5">
                      <X className="w-3 h-3" />
                    </button>
                  </span>
                ))}
              </div>
              <div className="flex gap-2">
                <Input
                  placeholder="Add competitor brand..."
                  value={newBrand}
                  onChange={(e) => setNewBrand(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' && newBrand.trim()) {
                      setCompetitorBrands([...competitorBrands, newBrand.trim()])
                      setNewBrand('')
                    }
                  }}
                />
                <Button variant="outline" size="icon" onClick={() => { if (newBrand.trim()) { setCompetitorBrands([...competitorBrands, newBrand.trim()]); setNewBrand('') } }}>
                  <Plus className="w-4 h-4" />
                </Button>
              </div>
            </div>
          </div>
        )}

        {/* Step 3: Searching & Scoring */}
        {currentStep === 3 && (
          <div className="text-center py-12">
            {isSearching || isScoring ? (
              <>
                <Loader2 className="h-16 w-16 text-primary mx-auto mb-4 animate-spin" />
                <h2 className="text-lg font-semibold mb-2">
                  {isScoring ? 'Scoring Ads...' : 'Searching...'}
                </h2>
                <p className="text-gray-600 mb-6">
                  {isScoring
                    ? `Scoring ${searchResults ? deduplicateAds([...searchResults.product_related, ...searchResults.competitor_brands]).length : 0} ads...`
                    : 'Searching ad library...'}
                </p>
                <div className="w-64 mx-auto bg-gray-200 rounded-full h-2">
                  <div className="bg-primary h-2 rounded-full animate-pulse" style={{ width: isScoring ? '70%' : '30%' }} />
                </div>
              </>
            ) : (
              <>
                <Sparkles className="h-16 w-16 text-primary mx-auto mb-4" />
                <h2 className="text-lg font-semibold mb-2">Ready to Search</h2>
                <p className="text-gray-600 mb-6">
                  We'll search for reference ads and score them based on your product
                </p>
                <Button size="lg" onClick={() => searchAdsMutation.mutate()}>
                  Search & Score Reference Ads
                </Button>
              </>
            )}
          </div>
        )}

        {/* Step 4: Select References - Updated with collapsible sections */}
        {currentStep === 4 && searchResults && (
          <div className="space-y-4">
            {/* Header */}
            <h2 className="text-lg font-semibold">Select Reference Ads</h2>

            {/* Selection Mode Toggle */}
            <div className="flex flex-col gap-4 p-4 bg-gray-50 rounded-lg">
              <div className="flex items-center justify-between">
                <SelectionModeToggle
                  mode={selectionMode}
                  onModeChange={handleModeChange}
                  disabled={isScoring}
                />
                <div className="text-sm text-gray-600">
                  Selected: <span className="font-medium">{selectedAdIds.length} / {MAX_SELECTION}</span>
                  <span className="ml-2 text-primary font-medium">
                    ({selectedAdIds.length} credit{selectedAdIds.length !== 1 ? 's' : ''})
                  </span>
                </div>
              </div>

              {/* Auto mode: count selector */}
              {selectionMode === 'auto' && (
                <div className="flex items-center justify-between">
                  <AdCountSelector
                    count={autoSelectCount}
                    onCountChange={handleAutoSelectCountChange}
                    disabled={isScoring}
                  />
                  <span className="text-xs text-gray-500">Sorted by score (highest first)</span>
                </div>
              )}

              {/* Manual mode: info text */}
              {selectionMode === 'manual' && (
                <p className="text-sm text-gray-500">
                  Click on ads to select or deselect them. Maximum {MAX_SELECTION} references allowed.
                </p>
              )}
            </div>

            {/* Product Related Section - Collapsible (deduplicated) */}
            {(() => {
              const { productAds, competitorAds } = getDeduplicatedAds()
              return (
                <>
                  {productAds.length > 0 && (
                    <CollapsibleAdSection
                      title="Product Related"
                      count={productAds.length}
                      expanded={expandProductRelated}
                      onToggle={() => setExpandProductRelated(!expandProductRelated)}
                      variant="product"
                    >
                      <div className="grid grid-cols-4 sm:grid-cols-5 md:grid-cols-6 gap-3">
                        {productAds.map((ad) => (
                          <ReferenceAdCard
                            key={ad.id}
                            ad={ad}
                            isSelected={selectedAdIds.includes(ad.id)}
                            onSelect={() => toggleAdSelection(ad.id)}
                            variant="product"
                            disabled={isScoring}
                          />
                        ))}
                      </div>
                    </CollapsibleAdSection>
                  )}

                  {/* Competitor Brands Section - Collapsible (deduplicated) */}
                  {competitorAds.length > 0 && (
                    <CollapsibleAdSection
                      title="From Competitor Brands"
                      count={competitorAds.length}
                      expanded={expandCompetitorBrands}
                      onToggle={() => setExpandCompetitorBrands(!expandCompetitorBrands)}
                      variant="competitor"
                    >
                      <div className="grid grid-cols-4 sm:grid-cols-5 md:grid-cols-6 gap-3">
                        {competitorAds.map((ad) => (
                          <ReferenceAdCard
                            key={ad.id}
                            ad={ad}
                            isSelected={selectedAdIds.includes(ad.id)}
                            onSelect={() => toggleAdSelection(ad.id)}
                            variant="competitor"
                            disabled={isScoring}
                          />
                        ))}
                      </div>
                    </CollapsibleAdSection>
                  )}

                  {/* Empty state */}
                  {productAds.length === 0 && competitorAds.length === 0 && (
                    <div className="text-center py-12 text-gray-500">
                      <p>No reference ads found. Try adjusting your keywords or competitor brands.</p>
                    </div>
                  )}
                </>
              )
            })()}
          </div>
        )}

        {/* Step 5: Configure */}
        {currentStep === 5 && (
          <div className="space-y-4">
            <h2 className="text-lg font-semibold mb-4">Configure Generation</h2>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Model</label>
              <select
                value={config.model}
                onChange={(e) => updateConfig({ model: e.target.value as ToolId })}
                className="w-full border rounded-md p-2 text-sm"
              >
                <option value="gemini_image">Gemini Image</option>
                <option value="gpt_image">GPT Image</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Image Size</label>
              <AspectRatioSelector
                value={config.aspectRatio}
                onChange={(value) => updateConfig({ aspectRatio: value })}
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">User Preferences (optional)</label>
              <textarea
                value={config.customPrompt}
                onChange={(e) => updateConfig({ customPrompt: e.target.value })}
                className="w-full border rounded-md p-2 h-24 text-sm"
                placeholder="Add any specific instructions..."
              />
            </div>
          </div>
        )}

        {/* Step 6: Generate */}
        {currentStep === 6 && (
          <div className="text-center py-12">
            {(taskStatus === 'processing' || taskStatus === 'pending') ? (
              // Processing state
              <>
                <Loader2 className="h-16 w-16 text-primary mx-auto mb-4 animate-spin" />
                <h2 className="text-lg font-semibold mb-2">Generating...</h2>
                <p className="text-gray-600 mb-6">
                  Creating {currentTask?.metadata?.count || selectedAdIds.length} image{(currentTask?.metadata?.count || selectedAdIds.length) !== 1 ? 's' : ''}
                </p>
                <div className="w-64 mx-auto bg-gray-200 rounded-full h-2">
                  <div className="bg-primary h-2 rounded-full animate-pulse" style={{ width: '60%' }} />
                </div>
              </>
            ) : taskStatus === 'failed' ? (
              // Failed state
              <>
                <AlertCircle className="h-16 w-16 text-red-500 mx-auto mb-4" />
                <h2 className="text-lg font-semibold mb-2 text-red-700">Generation Failed</h2>
                <p className="text-red-600 mb-6">{taskError || 'An error occurred'}</p>
                <Button onClick={() => { setIsGenerating(true); generateMutation.mutate() }} disabled={generateMutation.isPending || isGenerating}>
                  Try Again
                </Button>
              </>
            ) : taskStatus === 'completed' ? (
              // Completed state - grid display of generated images
              <>
                <Check className="h-12 w-12 text-green-500 mx-auto mb-3" />
                <h2 className="text-lg font-semibold mb-1">Generation Complete!</h2>
                {generatedResults.length > 0 ? (
                  <>
                    <p className="text-gray-600 mb-6">
                      {generatedResults.length} image{generatedResults.length !== 1 ? 's' : ''} generated
                    </p>

                    {/* Grid display - responsive 1/2/3 columns */}
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 max-w-3xl mx-auto mb-6">
                      {generatedResults.map((result: { url: string; reference_ad_id?: string }, idx: number) => (
                        <div
                          key={idx}
                          className="cursor-pointer group"
                          onClick={() => setSelectedResultIdx(idx)}
                        >
                          <img
                            src={result.url}
                            alt=""
                            className="w-full aspect-square object-cover rounded-lg border
                                       group-hover:ring-2 group-hover:ring-primary transition-all"
                          />
                          {result.reference_ad_id && (
                            <p className="mt-1 text-xs text-gray-500 group-hover:text-primary flex items-center gap-1 justify-center">
                              <LinkIcon className="w-3 h-3" /> Click to view reference
                            </p>
                          )}
                        </div>
                      ))}
                    </div>

                    {/* Result detail modal */}
                    {selectedResultIdx !== null && (
                      <ResultDetailModal
                        result={generatedResults[selectedResultIdx]}
                        referenceAd={
                          generatedResults[selectedResultIdx].reference_ad_id
                            ? scoredAdsMap[generatedResults[selectedResultIdx].reference_ad_id]
                            : null
                        }
                        currentIdx={selectedResultIdx}
                        total={generatedResults.length}
                        onClose={() => setSelectedResultIdx(null)}
                        onPrev={() => setSelectedResultIdx(Math.max(0, selectedResultIdx - 1))}
                        onNext={() => setSelectedResultIdx(Math.min(generatedResults.length - 1, selectedResultIdx + 1))}
                      />
                    )}
                  </>
                ) : (
                  <p className="text-gray-600 mb-6">Images have been saved to your library</p>
                )}
                <Button onClick={() => navigate('/library')}>View in Library</Button>
              </>
            ) : (
              // Initial state - ready to generate
              <>
                <Sparkles className="h-16 w-16 text-primary mx-auto mb-4" />
                <h2 className="text-lg font-semibold mb-2">Ready to Generate</h2>
                <p className="text-gray-600 mb-6">
                  Generating {selectedAdIds.length} image{selectedAdIds.length !== 1 ? 's' : ''} based on your selected references
                </p>
                <Button size="lg" onClick={() => { setIsGenerating(true); generateMutation.mutate() }} disabled={generateMutation.isPending || isGenerating}>
                  {generateMutation.isPending || isGenerating ? 'Starting...' : 'Start Generation'}
                </Button>
              </>
            )}
          </div>
        )}
      </div>

      {/* Navigation */}
      <div className="flex justify-between">
        <Button
          variant="outline"
          onClick={() => setCurrentStep(Math.max(1, currentStep - 1))}
          disabled={currentStep === 1}
        >
          <ChevronLeft className="h-4 w-4 mr-2" />
          Previous
        </Button>
        <Button onClick={handleNext} disabled={!canProceed() || isLoading || currentStep === 6}>
          {isLoading ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : null}
          Next
          <ChevronRight className="h-4 w-4 ml-2" />
        </Button>
      </div>
    </div>
  )
}
